import { Injectable, Injector } from '@angular/core';
import { AbcCore, ConvertHelper } from '@barthauer/abc/core';
import { QueryObjectsModel, QueryValueModel } from '@barthauer/abc/core/data';
import { OperaContext } from '@barthauer/abc/maintenance/opera/core';
import { ResourceManagerService, ResourceSet } from 'libs/abc/core/resources/src';
import { ObjectsSetsSettings, OrderUnit } from 'libs/abc/ui-frameworks/objects-sets/src/lib/objects-sets-column-info';

@Injectable()
export class BasysCoreService {
  private readonly currentMunicipalityVariantId = '$CurrentMunicipalityVariantId';
  private readonly objectSpaceName = 'BaSYS.Complete';
  private orderUnitsMap: Map<string, { Designation: string; Id: string }[]> = new Map();
  private abcCore: AbcCore;
  private resAsync: Promise<ResourceSet>;

  constructor(
    private injector: Injector,
    private resourceManagerService: ResourceManagerService,
  ) {
    this.abcCore = this.injector.get(AbcCore);
    this.resAsync = this.resourceManagerService.getResourceSet('basys-core');
  }

  public init() {
    this.setOperaContext();
    this.setContainerId();
    this.setOrderUnits();
  }

  private async orderUnitType(): Promise<OrderUnit[]> {
    let res: ResourceSet = ResourceSet.Empty;
    res = await this.resAsync;
    return [
      {
        name: 'Real Estate',
        caption: res.get('Objects-Set-RealEstate'),
        referencedObjectTypeName: this.getEnumKey(ObjectType.Liegenschaft),
        referencedObjectTypeValue: ObjectType.Liegenschaft,
        data: () => this.getDataByOrderUnitType('Liegenschaft'),
      },
      {
        name: 'Budget Unit',
        caption: res.get('Objects-Set-BudgetUnit'),
        referencedObjectTypeName: this.getEnumKey(ObjectType.Wirtschaftseinheit),
        referencedObjectTypeValue: ObjectType.Wirtschaftseinheit,
        data: () => this.getDataByOrderUnitType('Wirtschaftseinheit'),
      },
      {
        name: 'Drainage network',
        caption: res.get('Objects-Set-DrainageNetwork'),
        referencedObjectTypeName: this.getEnumKey(ObjectType.GemeindeVariante),
        referencedObjectTypeValue: ObjectType.GemeindeVariante,
        data: () => this.getDataByOrderUnitType('GemeindeVariante'),
      },
      {
        name: 'Other',
        caption: res.get('Objects-Set-Other'),
        referencedObjectTypeName: 'Other',
        data: () => this.getDataByOrderUnitType('Other'),
      },
    ];
  }

  private getEnumKey(enumValue): string {
    return ConvertHelper.getEnumKeyByEnumValue(ObjectType, enumValue);
  }

  private async getParentValue(): Promise<string> {
    const queryValueModel = new QueryValueModel();
    queryValueModel.OPath = this.currentMunicipalityVariantId;
    return (await this.abcCore.data.executeReadValueQuery(queryValueModel).toPromise()) as unknown as string;
  }

  private setContainerId() {
    ObjectsSetsSettings.getCurrentContainerId = () => this.getParentValue();
  }

  private async setOrderUnits() {
    ObjectsSetsSettings.orderUnits = await this.orderUnitType();
  }

  private async getDataByOrderUnitType(objectType: string): Promise<{ Designation: string; Id: string }[]> {
    if (!this.orderUnitsMap.has(objectType)) {
      let orderUnitData: { Designation: string; Id: string }[] = [];
      if (objectType !== 'Other') {
        orderUnitData = ConvertHelper.arrayBufferToObjectArray<{ Designation: string; Id: string }>(
          await this.abcCore.data.executeReadObjectsQuery(this.orderUnitQueryModel(objectType)).toPromise(),
        );
      } else {
        orderUnitData = [{ Designation: '', Id: null }];
      }
      this.orderUnitsMap.set(objectType, orderUnitData);
      return orderUnitData;
    } else {
      return this.orderUnitsMap.get(objectType);
    }
  }

  private orderUnitQueryModel(objectType: string): QueryObjectsModel {
    let opath = 'ParentId=$CurrentMunicipalityId';
    let isGemeindeVariante: boolean = false;

    if (objectType === this.getEnumKey(ObjectType.GemeindeVariante)) {
      opath = 'IdRefGemeindeParent=$CurrentMunicipalityId';
      isGemeindeVariante = true;
    }

    const query = this.abcCore.data.createSimpleQuery('', objectType, opath, [
      { Name: 'Designation', OPath: isGemeindeVariante ? 'ProjektVariante.Bezeichnung' : 'ref:Id' },
      { Name: 'Id', OPath: 'Id' },
    ]);
    return query;
  }

  private setOperaContext() {
    OperaContext.parentVarientId = this.currentMunicipalityVariantId;
    OperaContext.objectSpaceName = this.objectSpaceName;
    OperaContext.getOrderOpath = (currentUser: string) => this.getOrderOpath(currentUser);
  }

  private getOrderOpath(currentUser: string) {
    return `ParentId=$CurrentMunicipalityVariantId 
            And (Not (CaseNull(Status,1) In (0,3)) 
            Or CaseNull(Status, 0) = 0 And KkStatus In ('3','4','5')) 
            And (Exists(Properties[Designation='ExportBCEDocId' 
            And Not IsNull(TextValue)]) 
            And Not Exists(MtResourceOrders) 
            or Exists(MtResourceOrders.Resource${currentUser}))`;
  }
}

enum ObjectType {
  'Liegenschaft' = 210,
  'Wirtschaftseinheit' = 214,
  'GemeindeVariante' = 200000101,
}
